import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import type { SnowplowTitleContextGraphql, SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { ObjectType } from '@/@types/graphql-types';

type ContentType = 'articles' | 'lists' | 'other';

type EditorialTrackingSpecs =
	| ClickPlayOnTrailer
	| ClicksOnTitle
	| ClickArticleLink
	| ClickOnAuthor
	| ClickContentFilter
	| ClickOnAuthorLinks
	| ClickOnReadMore;
type TrackEditorial<T extends EditorialTrackingSpecs> = (event: T) => void;

const trackEditorial: TrackEditorial<EditorialTrackingSpecs> = event => {
	const { category, contexts = [], ...properties } = event;
	TrackingHelper.trackEvent(category, properties, contexts);
};

// AUTHOR
type ClickOnAuthor = Readonly<{
	category: 'persons_credit_name';
	action: 'clicked';
	label: 'AUTHOR';
	useLastRoute?: boolean;
	property: string;
	contexts?: [];
}>;
export const clickOnAuthor = (
	property: ClickOnAuthor['property'],
	contexts?: ClickOnAuthor['contexts'],
	useLastRoute?: boolean
) =>
	(trackEditorial as TrackEditorial<ClickOnAuthor>)({
		category: 'persons_credit_name',
		action: 'clicked',
		label: 'AUTHOR',
		property,
		contexts,
		useLastRoute,
	});

type ClickContentFilter = Readonly<{
	category: 'userinteraction';
	action: 'filter_toggled';
	label: 'content_types';
	property: ContentType;
	contexts?: [];
}>;
export const clickContentFilter = trackEditorial as TrackEditorial<ClickContentFilter>;

type ClickOnAuthorLinks = Readonly<{
	category: 'person_social_media' | 'person_publication';
	action: 'clicked';
	label: 'AUTHOR';
	property: string;
	contexts?: [];
}>;
export const clickOnAuthorLinks = (
	category: ClickOnAuthorLinks['category'],
	property: ClickOnAuthorLinks['property'],
	contexts?: ClickOnAuthorLinks['contexts']
) =>
	(trackEditorial as TrackEditorial<ClickOnAuthorLinks>)({
		category,
		property,
		action: 'clicked',
		label: 'AUTHOR',
		contexts,
	});

// NEWS & LIST
export type ClickArticleLink = Readonly<{
	category: 'userinteraction';
	action: 'content_clicked';
	/** URL of the clicked link. */
	property: string;
	/** Internal means the link points to JustWatch.com */
	label: 'internal' | 'external';
	contexts?: [];
}>;
export const clickArticleLink = trackEditorial as TrackEditorial<ClickArticleLink>;

// NEWS ARTICLE
type ClickPlayOnTrailer = Readonly<{
	category: 'youtube_playing';
	action: ObjectType;
	label: 'backdrop';
	contexts: [SnowplowTitleContextGraphql];
}>;
export const clickPlayOnTrailer = trackEditorial as TrackEditorial<ClickPlayOnTrailer>;

// LIST ARTICLE
type ClicksOnTitle = Readonly<{
	category: 'userinteraction';
	action: 'title_clicked';
	label: 'editorial-lists';
	contexts: [SnowplowTitleContextGraphql];
}>;
export const clicksOnTitle = trackEditorial as TrackEditorial<ClicksOnTitle>;

type ClickOnReadMore = Readonly<{
	category: 'content_header';
	action: 'expanded' | 'collapsed';
	contexts?: [];
}>;
export const clickOnReadMore = trackEditorial as TrackEditorial<ClickOnReadMore>;

// GUIDE PAGE
export type ClickGuideItemLink = Readonly<{
	property: 'editorial-list' | 'news' | 'title';
	label: string;
	value: number;
}>;
export const clickGuideItemLink = (event: ClickGuideItemLink, contexts?: SnowplowContext[]) => {
	TrackingHelper.trackEvent('userinteraction', { action: 'content_clicked', ...event }, contexts ?? []);
};
